import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

type UseDiscardChangesConfirmOptions = {
  /**
   * When true, Cancel/X opens the confirm dialog.
   * Defaults to false — only ask when there are unsaved changes.
   */
  when?: boolean;
  title?: string;
  description?: string;
  onSave?: () => void | Promise<void>;
  stayLabel?: string;
  discardLabel?: string;
  saveLabel?: string;
};

type RequestDiscardOptions = {
  /** Override whether to confirm. Defaults to the hook `when` option. */
  dirty?: boolean;
  onSave?: () => void | Promise<void>;
};

/**
 * Confirm before discarding in-progress edits (Cancel / X / dialog close).
 * Call `requestDiscard(action)` instead of running the exit action directly.
 */
export function useDiscardChangesConfirm({
  when = false,
  title = 'Save your changes?',
  description = 'Do you want to save your changes before leaving?',
  onSave: defaultOnSave,
  stayLabel = 'Keep editing',
  discardLabel = "Don't save",
  saveLabel = 'Save',
}: UseDiscardChangesConfirmOptions = {}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSave, setShowSave] = useState(Boolean(defaultOnSave));
  const pendingRef = useRef<(() => void) | null>(null);
  const pendingSaveRef = useRef<(() => void | Promise<void>) | null>(null);
  const whenRef = useRef(when);

  useEffect(() => {
    whenRef.current = when;
  }, [when]);

  const requestDiscard = useCallback(
    (action: () => void, options?: RequestDiscardOptions) => {
      const shouldConfirm = options?.dirty ?? whenRef.current;
      if (!shouldConfirm) {
        action();
        return;
      }
      pendingRef.current = action;
      const saveHandler = options?.onSave ?? defaultOnSave;
      pendingSaveRef.current = saveHandler ?? null;
      setShowSave(Boolean(saveHandler));
      setOpen(true);
    },
    [defaultOnSave]
  );

  const handleStay = useCallback(() => {
    pendingRef.current = null;
    pendingSaveRef.current = null;
    setOpen(false);
  }, []);

  const handleDiscard = useCallback(() => {
    const action = pendingRef.current;
    pendingRef.current = null;
    pendingSaveRef.current = null;
    setOpen(false);
    action?.();
  }, []);

  const handleSave = useCallback(async () => {
    const save = pendingSaveRef.current;
    if (!save) {
      handleDiscard();
      return;
    }
    setSaving(true);
    try {
      await save();
      pendingRef.current = null;
      pendingSaveRef.current = null;
      setOpen(false);
    } catch {
      // Keep dialog open; caller should toast errors
    } finally {
      setSaving(false);
    }
  }, [handleDiscard]);

  const dialog = (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving) handleStay();
      }}
    >
      <AlertDialogContent className="rounded-2xl z-[200]">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-2">
          <AlertDialogCancel disabled={saving} className="rounded-xl mt-0" onClick={handleStay}>
            {stayLabel}
          </AlertDialogCancel>
          <Button
            type="button"
            variant="outline"
            disabled={saving}
            className="rounded-xl"
            onClick={handleDiscard}
          >
            {discardLabel}
          </Button>
          {showSave ? (
            <AlertDialogAction
              disabled={saving}
              className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={(e) => {
                e.preventDefault();
                void handleSave();
              }}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {saveLabel}
            </AlertDialogAction>
          ) : null}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { requestDiscard, dialog, isConfirmOpen: open };
}
